import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { TextbookDrawerService, ToolName } from '@textbook-drawer';
import {
  ACTIVE_COLOR,
  CLEAR_CANVAS_COLOR,
  DISABLED_COLOR,
  DrawingUtilityService,
  INITIAL_SIZE,
} from '../../../../internal/drawing-utility/drawing-utility.service';
import { ActionNotesService } from '../../../../internal/actions/@base/services';
import { CssDirectives } from '../../../../internal/drawing-utility/components/drawer-background-tool/drawer-background-tool.component';

@Component({
  selector: 'txtb-sandbox-drawer',
  templateUrl: './sandbox-drawer.component.html',
  styleUrls: ['./sandbox-drawer.component.scss'],
  providers: [DrawingUtilityService, TextbookDrawerService, ActionNotesService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SandboxDrawerComponent implements OnDestroy {
  @Output() readonly goBack: EventEmitter<void> = new EventEmitter<void>();
  readonly ACTIVE_COLOR = ACTIVE_COLOR;
  readonly CLEAR_CANVAS_COLOR = CLEAR_CANVAS_COLOR;
  readonly DISABLED_COLOR = DISABLED_COLOR;
  readonly ToolName = ToolName;

  @ViewChild('drawerBox') set drawerBox({ nativeElement }: ElementRef) {
    this.drawingUtilityService.drawerBoxWidth = nativeElement.offsetWidth;
    this.drawingUtilityService.drawerBoxHeight = nativeElement.offsetHeight;
    this.size(INITIAL_SIZE);
  }

  readonly currentZoom$: Observable<number> = this.drawingUtilityService.currentZoom$;
  readonly backgroundStyles$: Observable<CssDirectives> =
    this.drawingUtilityService.backgroundStyles$;
  readonly color$: Observable<string> = this.drawingUtilityService.color$ubject;
  readonly size$: Observable<number> = this.drawingUtilityService.size$ubject;
  readonly textToolActive$: Observable<boolean> = this.drawingUtilityService.textToolActive$;
  readonly tool$ubject: BehaviorSubject<ToolName | undefined> =
    this.drawingUtilityService.tool$ubject;
  readonly tool$: Observable<ToolName | undefined> = this.drawingUtilityService.tool$;
  readonly drawingByPointerMove$: Observable<boolean> =
    this.drawingUtilityService.drawingByPointerMove$;
  readonly transparency$: Observable<number> = this.drawingUtilityService.transparency$;
  readonly visibility$: Observable<boolean> = this.drawingUtilityService.visibility$;
  readonly isCanvasDirty$: Observable<boolean> = this.drawingUtilityService.isCanvasDirty$;

  constructor(public drawingUtilityService: DrawingUtilityService) {}

  onDrawingAreaTouchStart(event: TouchEvent): void {
    this.drawingUtilityService.onDrawingAreaTouchStart(event);
  }

  toggleVisibility(): void {
    this.drawingUtilityService.toggleVisibility();
  }

  color(color: string): void {
    this.drawingUtilityService.setColor(color);
  }

  size(size: number): void {
    this.drawingUtilityService.setSize(size);
  }

  transparency(transparency: number): void {
    this.drawingUtilityService.setTransparency(transparency);
  }

  clearCanvas(): void {
    this.drawingUtilityService.clearCanvas();
  }

  toggleTool(tool: ToolName): void {
    this.drawingUtilityService.toggleTool(tool);
  }

  toggleTextTool(): void {
    this.drawingUtilityService.toggleTextTool();
  }

  updateTextToolActive(isActive: boolean): void {
    this.drawingUtilityService.updateTextToolActive(isActive);
  }

  back(): void {
    this.goBack.emit();
  }

  updateBackgroundStyles(styles: CssDirectives): void {
    this.drawingUtilityService.updateBackgroundStyles(styles);
  }

  ngOnDestroy(): void {
    this.drawingUtilityService.destroy();
  }
}
